import classNames from "classnames";
import { Calendar, CalendarProps } from "primereact/calendar";
import { FormEvent, Nullable } from "primereact/ts-helpers";
import React, { useMemo } from "react";
import { convertToUTCMidday } from "../../../utils";
import { getFsExcludeClassName } from "../../../utils/getFsExcludeClassName";
import { getInputTestIdByName } from "../../../utils/getInputTestIdByName";
import { WSInputBaseProps } from "../../types/forms";
import { detachFieldProps, WSField, WSFieldProps } from "../WSField/WSField";
import {
  WSInputContainer,
  WSInputContainerProps
} from "../WSInputContainer/WSInputContainer";
import { useIsMobile } from "../layout";
import styles from "./WSInputDate.module.scss";
import { formatDate } from "./utils";

export type WSInputDateProps = Omit<
  WSInputBaseProps<any, any>,
  "value" | "onChange"
> &
  Omit<WSInputContainerProps, "size" | "align"> &
  WSFieldProps &
  Omit<
    CalendarProps,
    "pt" | "prefix" | "size" | "color" | "selectionMode" | "value" | "onChange"
  > & {
    value: Nullable<Date>;
    onChange(
      value: Nullable<Date>,
      event: FormEvent<Date> | React.ChangeEvent<HTMLInputElement>
    ): void;
  };

const defaultPlaceholder = "MM/DD/YYYY";
const defaultPlaceholderTime = "MM/DD/YYYY HH:mm";

export const WSInputDate = React.forwardRef<HTMLInputElement, WSInputDateProps>(
  (props, ref) => {
    const {
      name,
      status,
      className,
      icon,
      iconRight,
      inputAction,
      disabled,
      value,
      onChange,
      inputClassName,
      placeholder: placeholderProp,
      fsExclude,
      ...otherProps
    } = props;

    const isMobile = useIsMobile();

    const { fieldProps, ...inputProps } = detachFieldProps(otherProps);

    const stringValue = useMemo(() => {
      if (!value) {
        return "";
      }

      return formatDate(value, inputProps.showTime);
    }, [value, inputProps.showTime]);

    const placeholder = useMemo(() => {
      if (placeholderProp) {
        return placeholderProp;
      }

      if (inputProps.showTime) {
        return defaultPlaceholderTime;
      }

      return defaultPlaceholder;
    }, [inputProps.showTime, placeholderProp]);

    return (
      <WSField {...fieldProps} status={status}>
        <WSInputContainer
          status={status}
          icon={icon}
          iconRight={iconRight}
          inputAction={inputAction}
          disabled={disabled}
          className={className}
        >
          {isMobile ? (
            <input
              name={name}
              type={inputProps.showTime ? "datetime-local" : "date"}
              className={classNames(
                styles.input,
                inputClassName,
                getFsExcludeClassName(fsExclude)
              )}
              value={stringValue}
              data-testid={getInputTestIdByName(name)}
              onChange={(event) => {
                const stringValue = inputProps.showTime
                  ? event.target.value
                  : event.target.value + "T12:00:00Z";
                const newDate = new Date(stringValue);

                if (newDate) {
                  onChange?.(newDate, event);
                } else {
                  onChange?.(null, event);
                }
              }}
            />
          ) : (
            <Calendar
              inputRef={ref}
              name={name}
              className={classNames(
                inputClassName,
                getFsExcludeClassName(fsExclude)
              )}
              placeholder={placeholder}
              value={value}
              onChange={(e) => {
                let newValue = e.value;

                if (newValue instanceof Date && !inputProps.showTime) {
                  convertToUTCMidday(newValue);
                }

                // Fire the onChange callback with the normalized date(s).
                onChange?.(newValue, e);
              }}
              pt={{
                input: { root: { className: styles.input } }
              }}
              data-testid={getInputTestIdByName(name)}
              baseZIndex={1200}
              {...(inputProps as any)}
            />
          )}
        </WSInputContainer>
      </WSField>
    );
  }
);

WSInputDate.displayName = "WSInputDate";
